import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Pages/MediaandReviews.css';

/* ══════════════════════════════════════
   GSAP LOADER
══════════════════════════════════════ */
function loadGSAP() {
  return new Promise((resolve) => {
    if (window.gsap && window.ScrollTrigger) { resolve(); return; }
    const s1 = document.createElement('script');
    s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
    s1.onload = () => {
      const s2 = document.createElement('script');
      s2.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';
      s2.onload = () => { window.gsap.registerPlugin(window.ScrollTrigger); resolve(); };
      document.head.appendChild(s2);
    };
    document.head.appendChild(s1);
  });
}

/* ══════════════════════════════════════
   PARTICLE CANVAS
══════════════════════════════════════ */
function ParticleField() {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d'); let W, H, pts, raf;
    const resize = () => { W = c.width = c.offsetWidth; H = c.height = c.offsetHeight; };
    const make = () => ({ x: Math.random() * W, y: Math.random() * H, vx: (Math.random() - .5) * .25, vy: (Math.random() - .5) * .25, r: Math.random() * 1.3 + .3, a: Math.random() * .35 + .07 });
    const init = () => { resize(); pts = Array.from({ length: 55 }, make); };
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      pts.forEach(p => { p.x += p.vx; p.y += p.vy; if (p.x < 0) p.x = W; if (p.x > W) p.x = 0; if (p.y < 0) p.y = H; if (p.y > H) p.y = 0; ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fillStyle = `rgba(47,128,237,${p.a})`; ctx.fill(); });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const dx = pts[i].x - pts[j].x, dy = pts[i].y - pts[j].y, d = Math.sqrt(dx * dx + dy * dy);
        if (d < 90) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.strokeStyle = `rgba(47,128,237,${.1 * (1 - d / 90)})`; ctx.lineWidth = .5; ctx.stroke(); }
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    window.addEventListener('resize', init);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', init); };
  }, []);
  return <canvas ref={ref} className="mr-canvas" aria-hidden />;
}

/* ══════════════════════════════════════
   SECTION HEADER
══════════════════════════════════════ */
function SectionHeader({ eyebrow, title, accent, sub }) {
  return (
    <div className="mr-section-header">
      {eyebrow && <span className="mr-eyebrow">{eyebrow}</span>}
      <h2 className="mr-section-title">
        {title} {accent && <span className="mr-accent">{accent}</span>}
      </h2>
      {sub && <p className="mr-section-sub">{sub}</p>}
    </div>
  );
}

/* ══════════════════════════════════════
   STAR RATING
══════════════════════════════════════ */
function Stars({ count = 5 }) {
  return (
    <div className="mr-stars" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={`mr-star ${i < count ? 'mr-star--on' : 'mr-star--off'}`}>★</span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════
   SVG ICONS
══════════════════════════════════════ */
const IconNewspaper = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="8" width="36" height="32" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M14 16h20M14 22h20M14 28h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <rect x="26" y="26" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);
const IconQuote = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 28V20a8 8 0 0 1 8-8h2v6h-2a2 2 0 0 0-2 2v8H10zM28 28V20a8 8 0 0 1 8-8h2v6h-2a2 2 0 0 0-2 2v8H28z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M10 28h8M28 28h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);
const IconPlay = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="2"/>
    <path d="M20 17l14 7-14 7V17z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" fill="currentColor" fillOpacity="0.3"/>
  </svg>
);
const IconMail = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="6" y="10" width="36" height="28" rx="3" stroke="currentColor" strokeWidth="2"/>
    <path d="M6 14l18 12L42 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const IconDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3v12M8 11l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ══════════════════════════════════════
   MARQUEE (press logos)
══════════════════════════════════════ */
function PressMarquee() {
  const outlets = [
    'TechWave Weekly', 'Startup Radar India', 'The AI Review',
    'Forbes India', 'YourStory', 'Inc42', 'Analytics India Mag',
    'The Ken', 'Business Standard', 'Economic Times Tech',
  ];
  const doubled = [...outlets, ...outlets];
  return (
    <div className="mr-marquee-wrap">
      <div className="mr-marquee-track">
        {doubled.map((name, i) => (
          <span key={i} className="mr-marquee-item">
            <span className="mr-marquee-dot" />
            {name}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const MediaReviews = () => {
  const pageRef = useRef(null);
  const [activeTab, setActiveTab] = useState('all');

  const stats = [
    { num: '3+',   label: 'Media Features' },
    { num: '98%',  label: 'Client Satisfaction' },
    { num: '40%',  label: 'Avg Cost Reduction' },
    { num: '60%',  label: 'Faster Processing' },
  ];

  const mediaMentions = [
    {
      outlet: 'TechWave Weekly',
      tag: 'Industry Analysis',
      color: '#2f80ed',
      initial: 'TW',
      quote: 'IncogniFlex Services is emerging as a category leader in the Human-in-the-Loop AI space — bridging machine precision with human intuition.',
      date: 'March 2025',
    },
    {
      outlet: 'Startup Radar India',
      tag: 'Company Feature',
      color: '#10b981',
      initial: 'SR',
      quote: 'One of India\'s most promising SaaS companies, turning the AIaaS + HaaS model into a powerhouse platform for global teams.',
      date: 'February 2025',
    },
    {
      outlet: 'The AI Review',
      tag: 'Tech Deep Dive',
      color: '#7c6fe0',
      initial: 'AI',
      quote: 'With its modular APIs and scalable human ops, IncogniFlex is quietly building the backbone of next-gen enterprise automation.',
      date: 'January 2025',
    },
  ];

  const reviews = [
    {
      stars: 5,
      quote: 'IncogniFlex gave us AI capabilities we couldn\'t build in-house — and paired them with human QA that made our workflows bulletproof.',
      name: 'Ravi M.',
      role: 'VP Operations',
      company: 'FinTech India',
      avatar: 'RM',
      color: '#2f80ed',
      tag: 'AIaaS + HaaS',
      metric: { value: '3×', label: 'workflow speed' },
    },
    {
      stars: 5,
      quote: 'Our customer support costs dropped by 40% after switching to their HaaS model. Real people. Real results.',
      name: 'Maria G.',
      role: 'CX Lead',
      company: 'E-commerce Europe',
      avatar: 'MG',
      color: '#10b981',
      tag: 'HaaS',
      metric: { value: '40%', label: 'cost reduction' },
    },
    {
      stars: 5,
      quote: 'Their AIaaS offering helped us launch a personalized recommendation engine in just weeks. Fast, scalable, and accurate.',
      name: 'Neha D.',
      role: 'Product Manager',
      company: 'HealthTech Startup',
      avatar: 'ND',
      color: '#f59e0b',
      tag: 'AIaaS',
      metric: { value: '4wk', label: 'to launch' },
    },
    {
      stars: 5,
      quote: 'The HITL integration was seamless. IncogniFlex\'s annotators are fast, accurate, and genuinely invested in data quality.',
      name: 'James T.',
      role: 'ML Engineering Lead',
      company: 'Autonomous Systems Co.',
      avatar: 'JT',
      color: '#7c6fe0',
      tag: 'Data Annotation',
      metric: { value: '99.2%', label: 'annotation accuracy' },
    },
    {
      stars: 5,
      quote: 'We scaled our ops team from 5 to 50 in two months without hiring a single FTE. IncogniFlex handled everything.',
      name: 'Priya R.',
      role: 'COO',
      company: 'Logistics SaaS',
      avatar: 'PR',
      color: '#ec4899',
      tag: 'HaaS Scaling',
      metric: { value: '10×', label: 'team capacity' },
    },
    {
      stars: 5,
      quote: 'The NLP API accuracy out-of-the-box was impressive. Support helped us fine-tune it for our domain in days.',
      name: 'David K.',
      role: 'CTO',
      company: 'LegalTech Platform',
      avatar: 'DK',
      color: '#56ccf2',
      tag: 'NLP / AIaaS',
      metric: { value: '91%', label: 'NLP accuracy' },
    },
  ];

  const caseStudies = [
    {
      emoji: '🚚',
      color: '#2f80ed',
      tag: 'Case Study · Logistics',
      title: 'AIaaS for Enterprise Automation',
      desc: 'How we helped a logistics platform cut processing time by 60% using our custom ML models and intelligent document automation pipeline.',
      metrics: [
        { val: '60%', label: 'Faster Processing' },
        { val: '3×',  label: 'Throughput' },
        { val: '4wk', label: 'Time to Deploy' },
      ],
    },
    {
      emoji: '🧠',
      color: '#7c6fe0',
      tag: 'Client Spotlight · AI-First Company',
      title: 'Building Human-Led QA Loops',
      desc: 'Why one AI-first company still chose IncogniFlex for their human review layer — and how HITL services improved output quality by 38%.',
      metrics: [
        { val: '38%', label: 'Quality Improvement' },
        { val: '99%', label: 'SLA Adherence' },
        { val: '2wk', label: 'Onboarding' },
      ],
    },
    {
      emoji: '🔮',
      color: '#10b981',
      tag: 'Interview · Founders',
      title: 'The Future of Hybrid Teams',
      desc: 'Where we see AI and humans working together in 2030 and beyond — our founders on the philosophy behind the AIaaS + HaaS model.',
      metrics: [
        { val: '2030', label: 'Vision Horizon' },
        { val: 'AI+H', label: 'Hybrid Model' },
        { val: '∞',    label: 'Potential' },
      ],
    },
  ];

  const tabs = [
    { id: 'all',     label: 'All Reviews' },
    { id: 'aiaas',  label: 'AIaaS' },
    { id: 'haas',   label: 'HaaS' },
  ];

  const filterMap = {
    all:   reviews,
    aiaas: reviews.filter(r => r.tag.toLowerCase().includes('ai')),
    haas:  reviews.filter(r => r.tag.toLowerCase().includes('haas') || r.tag.toLowerCase().includes('annotation') || r.tag.toLowerCase().includes('scaling')),
  };
  const visibleReviews = filterMap[activeTab] || reviews;

  useEffect(() => {
    loadGSAP().then(() => {
      const { gsap, ScrollTrigger } = window;

      gsap.fromTo('.mr-hero-content > *',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.2 }
      );

      gsap.utils.toArray('.mr-section-header').forEach(el => {
        gsap.fromTo(el,
          { opacity: 0, y: 32 },
          { opacity: 1, y: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 85%' }
          }
        );
      });

      gsap.utils.toArray('.mr-media-card').forEach((el, i) => {
        gsap.fromTo(el,
          { opacity: 0, x: i % 2 === 0 ? -50 : 50 },
          { opacity: 1, x: 0, duration: 0.65, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 82%' }
          }
        );
      });

      gsap.fromTo('.mr-review-card',
        { opacity: 0, y: 50, scale: 0.95 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, stagger: 0.08, ease: 'power3.out',
          scrollTrigger: { trigger: '.mr-reviews-grid', start: 'top 80%' }
        }
      );

      gsap.utils.toArray('.mr-case-card').forEach((el, i) => {
        gsap.fromTo(el,
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out',
            delay: i * 0.1,
            scrollTrigger: { trigger: el, start: 'top 82%' }
          }
        );
      });

      gsap.fromTo('.mr-contact-card',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: '.mr-contact-grid', start: 'top 82%' }
        }
      );

      gsap.fromTo('.mr-cta-inner > *',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: '.mr-cta-section', start: 'top 82%' }
        }
      );
    });
    return () => {
      if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <div className="mr-page" ref={pageRef}>

      {/* ════════════════════ HERO ════════════════════ */}
      <section className="mr-hero">
        <ParticleField />
        <div className="mr-grid-bg" aria-hidden />
        <div className="mr-glow mr-glow-l" aria-hidden />
        <div className="mr-glow mr-glow-r" aria-hidden />
        <div className="mr-hero-inner">
          <div className="mr-hero-content">
            <span className="mr-hero-eyebrow">
              <span className="mr-eyebrow-dot" />
              Media & Client Reviews · IncogniFlex Services
            </span>
            <h1 className="mr-hero-title">
              See What the World is{' '}
              <span className="mr-hero-accent">Saying About Us</span>
            </h1>
            <p className="mr-hero-sub">
              From industry leaders to early adopters — IncogniFlex Services is gaining recognition
              for redefining what AI and human collaboration can look like. Real results. Real voices.
            </p>
            <div className="mr-hero-tags">
              {['Media Coverage', 'Client Reviews', 'Case Studies', 'Press Kit'].map(t => (
                <span key={t} className="mr-hero-tag">{t}</span>
              ))}
            </div>
            <div className="mr-hero-actions">
              <a href="#reviews" className="mr-cta-btn primary">
                <span>Read Client Reviews</span>
                <span className="mr-btn-arrow">↓</span>
              </a>
              <a href="mailto:media@incogniflexservices.com" className="mr-cta-btn secondary">
                Media Inquiry
              </a>
            </div>
          </div>
        </div>
        <div className="mr-hero-cut" aria-hidden />
      </section>

      {/* ════════════════════ MARQUEE ════════════════════ */}
      <section className="mr-marquee-section">
        <div className="mr-marquee-label">As seen in</div>
        <PressMarquee />
      </section>

      {/* ════════════════════ STATS BAR ════════════════════ */}
      <section className="mr-stats-section">
        <div className="mr-container">
          <div className="mr-stats-grid">
            {stats.map((s, i) => (
              <div key={i} className="mr-stat">
                <div className="mr-stat-num">{s.num}</div>
                <div className="mr-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ IN THE MEDIA ════════════════════ */}
      <section className="mr-media-section">
        <div className="mr-container">
          <SectionHeader
            eyebrow="🗞️ In the Media"
            title="Industry Leaders Are"
            accent="Taking Notice"
            sub="Read what top publications are saying about IncogniFlex and our approach to AI-human collaboration."
          />
          <div className="mr-media-grid">
            {mediaMentions.map((m, i) => (
              <div key={i} className="mr-media-card" style={{ '--mc': m.color }}>
                <div className="mr-media-card-top">
                  <div className="mr-media-avatar" style={{ background: m.color + '20', borderColor: m.color + '44', color: m.color }}>
                    {m.initial}
                  </div>
                  <div className="mr-media-meta">
                    <span className="mr-media-outlet">{m.outlet}</span>
                    <span className="mr-media-tag" style={{ color: m.color, background: m.color + '15', borderColor: m.color + '35' }}>{m.tag}</span>
                  </div>
                  <span className="mr-media-date">{m.date}</span>
                </div>
                <div className="mr-media-quote-wrap">
                  <span className="mr-media-quote-mark" style={{ color: m.color }}>"</span>
                  <p className="mr-media-quote">{m.quote}</p>
                </div>
                <div className="mr-card-glow" style={{ background: m.color }} aria-hidden />
                <div className="mr-card-top-line" style={{ background: `linear-gradient(90deg, ${m.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>

          {/* Press CTA row */}
          <div className="mr-press-actions">
            <a href="#" className="mr-press-btn">
              <IconArrow />
              See All Press Mentions
            </a>
            <a href="#" className="mr-press-btn mr-press-btn--alt">
              <IconDownload />
              Download Media Kit (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* ════════════════════ CLIENT REVIEWS ════════════════════ */}
      <section className="mr-reviews-section" id="reviews">
        <div className="mr-container">
          <SectionHeader
            eyebrow="💬 What Our Clients Say"
            title="Trusted by Teams"
            accent="Around the World"
            sub="Real feedback from real clients. No cherry-picking — just the results our partners experience."
          />

          {/* Filter tabs */}
          <div className="mr-review-tabs">
            {tabs.map(t => (
              <button
                key={t.id}
                className={`mr-tab ${activeTab === t.id ? 'mr-tab--active' : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="mr-reviews-grid">
            {visibleReviews.map((r, i) => (
              <div key={i} className="mr-review-card" style={{ '--rc': r.color }}>
                {/* Top row: stars + metric */}
                <div className="mr-review-top">
                  <Stars count={r.stars} />
                  <div className="mr-review-metric" style={{ color: r.color }}>
                    <span className="mr-metric-val">{r.metric.value}</span>
                    <span className="mr-metric-label">{r.metric.label}</span>
                  </div>
                </div>

                {/* Tag */}
                <span className="mr-review-tag" style={{ color: r.color, background: r.color + '15', borderColor: r.color + '35' }}>
                  {r.tag}
                </span>

                {/* Quote */}
                <blockquote className="mr-review-quote">
                  <span className="mr-review-mark" style={{ color: r.color }}>"</span>
                  {r.quote}
                </blockquote>

                {/* Author */}
                <div className="mr-review-author">
                  <div className="mr-author-avatar" style={{ background: r.color + '20', borderColor: r.color + '55', color: r.color }}>
                    {r.avatar}
                  </div>
                  <div>
                    <span className="mr-author-name">{r.name}</span>
                    <span className="mr-author-role">{r.role} · {r.company}</span>
                  </div>
                </div>

                <div className="mr-card-glow" style={{ background: r.color }} aria-hidden />
                <div className="mr-card-top-line" style={{ background: `linear-gradient(90deg, ${r.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ CASE STUDIES ════════════════════ */}
      <section className="mr-cases-section">
        <div className="mr-container">
          <SectionHeader
            eyebrow="🎥 Featured Stories"
            title="Impact You Can"
            accent="Measure"
            sub="Deep dives into how IncogniFlex clients achieved real, quantifiable outcomes."
          />
          <div className="mr-cases-grid">
            {caseStudies.map((c, i) => (
              <div key={i} className="mr-case-card" style={{ '--cc': c.color }}>
                <div className="mr-case-top">
                  <div className="mr-case-emoji-wrap" style={{ background: c.color + '15', borderColor: c.color + '35' }}>
                    <span className="mr-case-emoji">{c.emoji}</span>
                  </div>
                  <span className="mr-case-tag" style={{ color: c.color, background: c.color + '12', borderColor: c.color + '30' }}>
                    {c.tag}
                  </span>
                </div>
                <h3 className="mr-case-title">{c.title}</h3>
                <p className="mr-case-desc">{c.desc}</p>
                <div className="mr-case-metrics">
                  {c.metrics.map((m, mi) => (
                    <div key={mi} className="mr-case-metric">
                      <span className="mr-case-metric-val" style={{ color: c.color }}>{m.val}</span>
                      <span className="mr-case-metric-label">{m.label}</span>
                    </div>
                  ))}
                </div>
                <a href="#" className="mr-case-link" style={{ color: c.color }}>
                  Read Full Story <span>→</span>
                </a>
                <div className="mr-card-glow" style={{ background: c.color }} aria-hidden />
                <div className="mr-card-top-line" style={{ background: `linear-gradient(90deg, ${c.color}, transparent)` }} aria-hidden />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════ SHARE YOUR STORY ════════════════════ */}
      <section className="mr-contact-section">
        <div className="mr-container">
          <SectionHeader
            eyebrow="🤝 Want to Share Your Story?"
            title="We'd Love to"
            accent="Hear From You"
            sub="Happy client? Media inquiry? Let's connect and tell your story to the world."
          />
          <div className="mr-contact-grid">
            <div className="mr-contact-card" style={{ '--cc': 'var(--blue)' }}>
              <div className="mr-contact-icon-wrap" style={{ borderColor: 'rgba(47,128,237,0.44)', background: 'rgba(47,128,237,0.15)', color: 'var(--blue)' }}>
                <IconMail />
              </div>
              <div className="mr-contact-body">
                <span className="mr-contact-label">Media Inquiries</span>
                <a href="mailto:media@incogniflexservices.com" className="mr-contact-value" style={{ color: 'var(--cyan)' }}>
                  media@incogniflexservices.com
                </a>
                <span className="mr-contact-sub">Press coverage, interviews, media kits</span>
              </div>
              <div className="mr-card-top-line" style={{ background: 'linear-gradient(90deg, var(--blue), transparent)' }} aria-hidden />
            </div>
            <div className="mr-contact-card" style={{ '--cc': 'var(--green)' }}>
              <div className="mr-contact-icon-wrap" style={{ borderColor: 'rgba(16,185,129,0.44)', background: 'rgba(16,185,129,0.15)', color: 'var(--green)' }}>
                <IconQuote />
              </div>
              <div className="mr-contact-body">
                <span className="mr-contact-label">Client Reviews</span>
                <a href="mailto:reviews@incogniflexservices.com" className="mr-contact-value" style={{ color: 'var(--cyan)' }}>
                  reviews@incogniflexservices.com
                </a>
                <span className="mr-contact-sub">Share your success story or testimonial</span>
              </div>
              <div className="mr-card-top-line" style={{ background: 'linear-gradient(90deg, var(--green), transparent)' }} aria-hidden />
            </div>
            <div className="mr-contact-card mr-contact-card--kit" style={{ '--cc': 'var(--purple)' }}>
              <div className="mr-contact-icon-wrap" style={{ borderColor: 'rgba(124,111,224,0.44)', background: 'rgba(124,111,224,0.15)', color: 'var(--purple)' }}>
                <IconDownload />
              </div>
              <div className="mr-contact-body">
                <span className="mr-contact-label">Press Kit</span>
                <span className="mr-contact-value" style={{ color: 'var(--text-1)' }}>Download Media Kit</span>
                <span className="mr-contact-sub">Logos, founder bio, boilerplate & brand assets</span>
              </div>
              <a href="#" className="mr-kit-btn">Download PDF →</a>
              <div className="mr-card-top-line" style={{ background: 'linear-gradient(90deg, var(--purple), transparent)' }} aria-hidden />
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════ CTA ════════════════════ */}
      <section className="mr-cta-section">
        <div className="mr-cta-glow" aria-hidden />
        <div className="mr-cta-grid-bg" aria-hidden />
        <div className="mr-container">
          <div className="mr-cta-inner">
            <span className="mr-cta-eyebrow">
              <span className="mr-eyebrow-dot" />
              Join the Story
            </span>
            <h2 className="mr-cta-title">
              Ready to Write Your Own <span className="mr-accent">Success Story?</span>
            </h2>
            <p className="mr-cta-sub">
              Join the growing list of enterprises, startups, and innovators transforming their operations with IncogniFlex.
            </p>
            <div className="mr-cta-actions">
              <Link to="/contact" className="mr-cta-btn primary">
                <span>Talk to an Expert</span>
                <span className="mr-btn-arrow">→</span>
              </Link>
              <Link to="/services" className="mr-cta-btn secondary">Explore Services</Link>
              <a href="mailto:media@incogniflexservices.com" className="mr-cta-btn secondary">Media Inquiry</a>
            </div>
            <p className="mr-cta-note">media@incogniflexservices.com · reviews@incogniflexservices.com</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default MediaReviews;